<template>
  <modal-dialog
    :modal-open-status="canDisplayDepartmentSelector ? canDisplayDepartmentSelector : false"
    :showCloseButton="true"
    @close="closeModal"
  >
    <template #header>
      <div class="text-primary-default">
        <h2 class="text-lg font-bold my-2">
          Welkom {{ currentUserIdentity?.initials }} {{ currentUserIdentity?.lastName }}
          {{ currentUserIdentity?.prePostion }}
        </h2>
        <div class="text-base font-semibold">Selecteer hieronder voor welke afdeling/locatie u in wilt loggen:</div>
      </div>
    </template>
    <template #body>
      <div
        class="justify-start md:flex md:flex-shrink-0 flex-1 md:justify-end"
        v-if="normalizedDepartments && normalizedDepartments.length > 0"
      >
        <BaseSelect :data="normalizedDepartments" v-model="selected" />
      </div>
    </template>
    <template #footer>
      <button
        @click.prevent="onSelectDepartment"
        class="flex justify-center py-2 px-2 md:px-4 border border-primary-default shadow-sm text-sm font-medium text-white bg-primary-default rounded-full hover:bg-primary-default/50 items-center focus:outline-none"
      >
        <span class="md:ml-1">Selecteer</span>
      </button>
    </template>
  </modal-dialog>
</template>
<script>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { combineLatest } from 'rxjs';
import { availableDepartments$, currentUserIdentity$, activeDepartment$ } from '@/services';
import router from '@/router';
import { pushDepartmentChangedEvent } from '@/components/sections/navigation/departmentChangedEventStream';

export default {
  name: 'Departments',
  emits: ['close'],
  setup(_, { emit }) {
    const store = useStore();

    const availableDepartments = ref(null);
    const activeDepartment = ref(null);
    const currentUserIdentity = ref(null);
    const redirectedFromRegistration = window.localStorage.getItem('redirectedFromRegistration');
    let subscription;

    onMounted(() => {
      subscription = combineLatest([currentUserIdentity$, availableDepartments$, activeDepartment$]).subscribe(
        ([user, departments, activeDept]) => {
          currentUserIdentity.value = user;
          availableDepartments.value = departments || [];
          activeDepartment.value = activeDept;
        }
      );
    });

    onBeforeUnmount(() => {
      subscription.unsubscribe();
    });

    const findSelected = (groups) => {
      for (const group of groups) {
        const found = group.subGroup?.find((i) => i.current);
        if (found) {
          return { item: found, groupTitle: group.groupTitle };
        }
      }

      const first = groups.find((g) => g.subGroup?.length);
      return first ? { item: first.subGroup[0], groupTitle: first.groupTitle } : { item: null, groupTitle: null };
    };

    const groupDepartments = (data) => {
      const groups = {};

      data.forEach((item) => {
        const index = item.title.lastIndexOf(' - ');

        if (index === -1) {
          groups[item.title] = groups[item.title] || {
            groupTitle: item.title,
            subGroup: []
          };
          groups[item.title].subGroup.push(item);
          return;
        }

        const departmentTitle = item.title.slice(0, index).trim();
        const groupTitle = item.title.slice(index + 3).trim();

        groups[groupTitle] = groups[groupTitle] || {
          groupTitle,
          subGroup: []
        };

        groups[groupTitle].subGroup.push({
          ...item,
          title: departmentTitle
        });
      });

      return Object.values(groups).sort((a, b) => a.groupTitle.localeCompare(b.groupTitle));
    };

    const normalizedDepartments = computed(() => {
      if (!availableDepartments.value?.length) return [];

      const mapped = availableDepartments.value.map(({ ppwRecId, name }) => ({
        ppwRecId,
        id: 'department-selector',
        title: name
          .toLowerCase()
          .split(' ')
          .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
          .join(' '),
        current: ppwRecId === activeDepartment.value?.ppwRecId
      }));

      return mapped.length > 1 ? groupDepartments(mapped) : [];
    });
    const canDisplayDepartmentSelector = computed(() => availableDepartments.value?.length > 1);
    const selected = ref(findSelected(normalizedDepartments.value));

    const closeModal = () => {
      emit('close');
      window.localStorage.removeItem('redirectedFromRegistration');
    };

    const onSelectDepartment = async () => {
      await store.dispatch('login/selectedDepartmentChange', selected.value?.item?.ppwRecId);
      if (redirectedFromRegistration === false) {
        router.push('/');
      }
      closeModal();
      await Promise.all([
        store.dispatch('navigation/fetchDepotMaterialDetails'),
        store.dispatch('navigation/fetchResults'),
        store.dispatch('news/fetchNewsItems', 3)
      ]);
      pushDepartmentChangedEvent({});
    };

    return {
      availableDepartments,
      currentUserIdentity,
      canDisplayDepartmentSelector,
      redirectedFromRegistration,
      closeModal,
      onSelectDepartment,
      normalizedDepartments,
      selected,
      findSelected
    };
  }
};
</script>
